/**
 * A caixa que interrompe a tela com o que está vencendo.
 *
 * Papel: desenha, na caixa de diálogo central de 480 px, a lista de
 * vencimentos — o mesmo título de `tituloDoAviso`, a mesma ordem de
 * `ordenarPorUrgencia` e a mesma frase de `prazoPorExtenso` —, com o prazo de
 * quem já venceu em negrito vermelho, e no rodapé o botão "Fechar" e, quando
 * quem monta pede, um segundo botão que leva a outra tela.
 *
 * Como usar: quem monta decide quando a caixa abre e guarda esse estado em
 * `aberto`; o componente não sabe de carregamento nem de navegação. Por isso
 * a regra de abrir uma vez só pertence a quem monta: uma página que passasse
 * `aberto` verdadeiro a cada montagem faria a caixa reaparecer toda vez que
 * alguém voltasse a ela. `onFechar` é chamado tanto pelo "Fechar" quanto pelo
 * `Esc` e pelo clique no véu, e é nele que quem monta deverá registrar que a
 * caixa já foi vista.
 *
 * Com a lista vazia a caixa não abre, ainda que `aberto` venha verdadeiro: uma
 * caixa sem item nenhum interromperia a tela para dizer nada.
 *
 * O item com `lembrete` — a carteirinha vencida — abre com o lembrete em
 * negrito vermelho no lugar do prazo e a validade anterior logo depois dele, e
 * não repete a validade no fim.
 *
 * O segundo botão só aparece quando `rotuloAcao` e `onAcao` chegam juntos. O
 * texto dele vem de fora, e não daqui, porque nomeia uma tela de um módulo, e
 * este componente não conhece módulo nenhum; quem monta também decide não
 * passá-lo à pessoa que não pode abrir aquela tela, para não oferecer um
 * caminho que termina em recusa.
 *
 * As linhas não são links: a caixa tem um destino só, no botão, e quem quiser
 * resolver um item de outro módulo fecha a caixa e vai à tela dele.
 */
import { useId } from 'react';

import { formatarData } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import {
  type ItemVencendo,
  ordenarPorUrgencia,
  prazoPorExtenso,
  tituloDoAviso,
} from '@/shared/lib/vencimento';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

interface PopupVencimentosProps {
  /** O que está vencendo, de todos os módulos, em qualquer ordem. */
  itens: readonly ItemVencendo[];
  /** Se a caixa deve estar aberta; quem monta guarda esse estado. */
  aberto: boolean;
  /** Chamado pelo "Fechar", pelo `Esc` e pelo clique no véu. */
  onFechar: () => void;
  /** Texto do segundo botão; sem ele, ou sem `onAcao`, o botão não aparece. */
  rotuloAcao?: string;
  /** Chamado pelo segundo botão; quem monta fecha a caixa e navega. */
  onAcao?: () => void;
  className?: string;
}

/**
 * A caixa de vencimentos.
 *
 * O foco vai para o "Fechar" quando a caixa abre, por ser o primeiro botão do
 * rodapé, e a lista inteira é a descrição acessível da caixa: o leitor de tela
 * lê o título e, em seguida, os itens.
 *
 * @param itens - O que está vencendo, já apurado, em qualquer ordem; a
 *   ordenação por urgência é feita aqui.
 * @param aberto - Se a caixa deve aparecer; com `itens` vazio ela não aparece
 *   de qualquer jeito.
 * @param onFechar - Chamado sempre que a caixa é fechada sem o segundo botão.
 * @param rotuloAcao - Texto do segundo botão, já com o nome da tela de destino.
 * @param onAcao - Chamado pelo segundo botão, que não fecha a caixa sozinho.
 * @param className - Classes de quem chama, aplicadas à caixa depois das
 *   internas pelo `cn()` e por isso capazes de sobrescrevê-las.
 * @returns A caixa de diálogo, aberta ou fechada.
 */
export function PopupVencimentos({
  itens,
  aberto,
  onFechar,
  rotuloAcao,
  onAcao,
  className,
}: PopupVencimentosProps) {
  const idDaLista = useId();

  /** Repassa ao `onFechar` o fechamento pedido pelo `Esc` ou pelo véu. */
  function handleOpenChange(abrir: boolean) {
    if (!abrir) onFechar();
  }

  return (
    <Dialog open={aberto && itens.length > 0} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby={idDaLista} className={cn('w-[480px]', className)}>
        <DialogHeader>
          <DialogTitle>{tituloDoAviso(itens)}</DialogTitle>
        </DialogHeader>

        <ul
          id={idDaLista}
          className="mb-1 list-disc pl-5 text-[13px] leading-[1.7] text-foreground"
        >
          {ordenarPorUrgencia(itens).map((item) => (
            <li key={item.id} className="my-1">
              {item.vencido ? (
                <strong className="font-semibold text-perigo">{prazoPorExtenso(item)}</strong>
              ) : (
                prazoPorExtenso(item)
              )}
              {item.lembrete !== undefined &&
                ` (validade anterior: ${formatarData(item.validade)})`}
              {' — '}
              {item.referente}: {item.rotulo}
              {item.lembrete === undefined && ` (${formatarData(item.validade)})`}
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>
            Fechar
          </Button>
          {rotuloAcao !== undefined && onAcao !== undefined && (
            <Button onClick={onAcao}>{rotuloAcao}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
